import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, LogOut } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

export const Navbar = ({ cartCount = 0 }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/bespoke', label: 'Bespoke' },
    { path: '/process', label: 'Process' },
    { path: '/shop', label: 'Shop' },
    { path: '/work', label: 'Work' },
    { path: '/blogs', label: 'Blogs' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'glass border-b border-brand-pearl/10' : 'bg-transparent'
          }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3" data-testid="navbar-logo">
              <img
                src="logos/rudralogo.png"
                alt="Rudra Apparel"
                className="h-10 w-auto object-contain"
              /> {/* <div className="w-10 h-10 rounded-sm border-2 border-brand-pearl flex items-center justify-center">
                <span className="text-xl font-heading font-bold text-brand-pearl">R</span>
              </div>
              <span className="text-xl font-heading font-semibold text-brand-pearl hidden sm:block">
                Rudra Apparel
              </span> */}
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                  className={`text-sm font-medium transition-colors ${location.pathname === link.path
                      ? 'text-brand-pearl'
                      : 'text-brand-walnut hover:text-brand-pearl'
                    }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA & Cart */}
            <div className="flex items-center space-x-4">
              <Link
                to="/contact"
                data-testid="get-quote-btn"
                className="hidden md:block px-6 py-2.5 bg-brand-pearl text-brand-ivory text-sm font-medium rounded-sm hover:bg-brand-walnut transition-colors"
              >
                Get a Quote
              </Link>

              {/* User Menu */}
              {isAuthenticated ? (
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center space-x-2 p-2 rounded-sm hover:bg-brand-timberwolf transition-colors"
                    data-testid="user-menu-btn"
                  >
                    {user?.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-brand-pearl flex items-center justify-center">
                        <User className="w-4 h-4 text-brand-ivory" />
                      </div>
                    )}
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-sm border border-brand-pearl/10 shadow-lg py-2 z-50" data-testid="user-dropdown">
                      <div className="px-4 py-2 border-b border-brand-pearl/10">
                        <p className="text-sm font-medium text-brand-pearl">{user?.name}</p>
                        <p className="text-xs text-brand-walnut">{user?.email}</p>
                      </div>
                      <button
                        onClick={async () => {
                          await logout();
                          setUserMenuOpen(false);
                          navigate('/');
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-brand-walnut hover:bg-brand-timberwolf flex items-center space-x-2"
                        data-testid="logout-btn"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  data-testid="login-btn"
                  className="hidden md:flex items-center space-x-2 px-4 py-2 border border-brand-pearl text-brand-pearl text-sm font-medium rounded-sm hover:bg-brand-pearl hover:text-brand-ivory transition-colors"
                >
                  <User className="w-4 h-4" />
                  <span>Login</span>
                </Link>
              )}

              <Link to="/cart" className="relative p-2" data-testid="cart-icon">
                <ShoppingCart className="w-5 h-5 text-brand-pearl" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-brand-wine text-white text-xs rounded-full flex items-center justify-center" data-testid="cart-count">
                    {cartCount}
                  </span>
                )}
              </Link>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="lg:hidden p-2"
                data-testid="mobile-menu-btn"
              >
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'tween' }}
            className="fixed top-20 left-0 right-0 bottom-0 bg-brand-ivory z-40 lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col p-8 space-y-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileOpen(false)}
                  className="text-lg font-medium text-brand-walnut hover:text-brand-pearl transition-colors"
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => setMobileOpen(false)}
                className="inline-block px-6 py-3 bg-brand-pearl text-brand-ivory text-center font-medium rounded-sm"
                data-testid="mobile-get-quote-btn"
              >
                Get a Quote
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};